/** @jsxImportSource @emotion/react */

// web/src/features/event/components/eventCardStyles.tsx
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Icon from 'src/components/Icon'
import {
  font,
  paragraphSmall,
  paragraphMedium,
  paragraphXSmall,
  weightBold,
  weightSemiBold,
} from 'src/components/css'

/**
 * Shared card chrome for the event detail page.
 *
 * The page splits into two cards (Details on the left, Location on the right)
 * that must look identical — extracting the row primitive and styles keeps them
 * from drifting apart.
 */

/** One labelled row, with a leading icon that identifies the field at a glance. */
export const DetailRow: React.FC<{
  iconName: string
  label: string
  children: React.ReactNode
}> = ({ iconName, label, children }) => (
  <div css={rowStyle}>
    <span css={rowIconStyle}>
      <Icon name={iconName} fontSize={14} color={colors.PRIMARY} />
    </span>
    <div css={rowContentStyle}>
      <span css={labelStyle}>{capitalizeWords(label)}</span>
      {children}
    </div>
  </div>
)

/* Matches the game overview's section labels (see components/SignInSheetCard)
   — grey, uppercase, wide letterspacing — so event and game screens share one
   section-heading treatment. */
export const sectionLabelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
  margin: 0 0 12px;
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
`

export const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SOFT_STEEL};
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

/* Fixed-width so every row's text starts on the same left edge, whatever the
   glyph's intrinsic width. */
const rowIconStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
  padding-top: 2px;
`

const rowContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

/* TESTING: NEUTRAL_50 (#c1c2c3) instead of NEUTRAL_0 (#ffffff) — a softer grey
   label that lets the white value carry the emphasis. Swap back to NEUTRAL_0
   for the previous near-white treatment. Shared by both cards, so this is the
   one place to change it. */
const labelStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightBold}
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

export const valueStyle = css`
  ${font.body}
  ${paragraphMedium}
  color: ${colors.WHITE};
  /* A long unbroken token (a pasted URL, a long street) should break rather
     than widen the column. */
  overflow-wrap: anywhere;
`

/* Secondary line beneath a value — the event's own timezone, or a repeat
   sub-label. Quieter, so it reads as a clarification rather than a second fact. */
export const secondaryTextStyle = css`
  display: block;
  ${font.body}
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
  margin-top: 2px;
`
